// 근거 보기 팝업: 왼쪽 "내가 넣은 내용", 오른쪽 "생기부". 문장을 누르면 쓴 내용이, 내용을 누르면 쓴 문장이 보인다.
// 고른 문장만 고칠 수 있다(다른 표현·짧게·요청·직접·빼기). 이어진 문장을 못 찾은 내용은 "이 내용 넣기".
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '../../common/Dialog';
import { calculateNEISBytes } from '../../../utils/neisBytes';
import { trackSaenggibuUxEvent } from '../../../utils/eventTracker';
import {
  buildPieces,
  describeHow,
  indexUnits,
  numberUnits,
  orderedSources,
  sentenceNumbers,
  sentencesByUnit,
} from './saenggibuEvidence';
import './SaenggibuEvidence.css';

const FILE_UNITS_VISIBLE = 6;

const REVISE_ACTIONS = [
  { id: 'reword', label: '다른 표현으로', instruction: '뜻과 사실은 그대로 두고 표현만 바꿔 주세요.' },
  { id: 'shorter', label: '짧게', instruction: '핵심 사실만 남기고 더 짧게 줄여 주세요.' },
  { id: 'request', label: '요청해서 고치기' },
  { id: 'manual', label: '직접 고치기' },
  { id: 'remove', label: '이 문장 빼기' },
];

const sourceTitle = (source) => source.label || '넣은 내용';

function SourceList({ sources, numbers, usedBy, selectedUnit, highlighted, onSelectUnit, onInsert, canRevise, busy }) {
  const [expanded, setExpanded] = useState({});
  return (
    <div className="sgev-sources">
      {orderedSources(sources).map((source) => {
        const isDirection = source.role === 'direction';
        const units = source.units || [];
        const isFile = source.kind === 'file';
        const used = units.filter((unit) => numbers.has(unit.id));
        const showAll = !isFile || expanded[source.id] || units.length <= FILE_UNITS_VISIBLE;
        const visible = showAll ? units : (used.length ? used : units.slice(0, FILE_UNITS_VISIBLE));
        const hiddenCount = units.length - visible.length;
        return (
          <section key={source.id} className="sgev-source" data-testid="sgev-source">
            <header className="sgev-source__head">
              <span className="sgev-source__label">{sourceTitle(source)}</span>
              {isDirection && <span className="sgev-tag">방향 참고</span>}
              {isFile && (
                <span className="sgev-source__count">{used.length > 0 ? `${used.length}곳을 썼어요` : '이어진 문장을 못 찾았어요'}</span>
              )}
            </header>
            <ul className="sgev-units">
              {visible.map((unit) => {
                const number = numbers.get(unit.id);
                const isSelected = selectedUnit === unit.id;
                const isLit = highlighted.has(unit.id);
                const canInsert = canRevise && !number && !isDirection;
                return (
                  <li key={unit.id}>
                    <button
                      type="button"
                      className={[
                        'sgev-unit',
                        number ? 'sgev-unit--used' : 'sgev-unit--unused',
                        isLit && 'is-lit',
                        isSelected && 'is-selected',
                      ].filter(Boolean).join(' ')}
                      aria-pressed={isSelected}
                      onClick={() => onSelectUnit(isSelected ? null : unit.id)}
                      data-testid="sgev-unit"
                    >
                      <span className={`sgev-num${number ? '' : ' sgev-num--empty'}`} aria-hidden="true">{number || ''}</span>
                      <span className="sgev-unit__text">{unit.text}</span>
                      {number && (usedBy.get(unit.id) || []).length > 1 && (
                        <span className="sgev-unit__meta">{usedBy.get(unit.id).length}문장</span>
                      )}
                    </button>
                    {isSelected && !number && (
                      <div className="sgev-unit__unused">
                        <span>생기부에서 이 내용을 못 찾았어요</span>
                        {canInsert && (
                          <button type="button" className="sgev-link-btn" disabled={busy} onClick={() => onInsert(unit)}>
                            이 내용 넣기
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            {hiddenCount > 0 && (
              <button type="button" className="sgev-more" onClick={() => setExpanded((prev) => ({ ...prev, [source.id]: true }))}>
                {used.length ? '나머지 부분' : '파일 내용'} {hiddenCount}곳 더 보기
              </button>
            )}
          </section>
        );
      })}
      {sources.length === 0 && (
        <p className="sgev-empty">넣은 내용을 찾지 못했어요. 다시 만들면 근거가 함께 저장돼요.</p>
      )}
    </div>
  );
}

export default function SaenggibuEvidenceDialog({
  open,
  onClose,
  subtitle,
  content,
  evidence,
  status,
  initialSentence = null,
  nav = null,
  canRevise = false,
  onRequestRevision,
  onApply,
  surface = 'single',
}) {
  const [selectedSentence, setSelectedSentence] = useState(initialSentence);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [tab, setTab] = useState('result');
  const [revise, setRevise] = useState(null);
  const [requestText, setRequestText] = useState('');
  const [manualDraft, setManualDraft] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const item = evidence?.item || null;
  const sources = useMemo(() => evidence?.sources || [], [evidence]);
  const pieces = useMemo(() => (item ? buildPieces(content, item) : null), [content, item]);
  const numbers = useMemo(() => numberUnits(item, sources), [item, sources]);
  const unitsById = useMemo(() => indexUnits(sources), [sources]);
  const usedBy = useMemo(() => sentencesByUnit(item), [item]);
  const sentencePieces = useMemo(() => (pieces || []).filter((piece) => piece.type === 'sentence'), [pieces]);
  const current = selectedSentence !== null ? sentencePieces[selectedSentence] : null;
  const busy = Boolean(revise?.loading);

  useEffect(() => {
    if (!open) return;
    setSelectedSentence(initialSentence);
    setSelectedUnit(null);
    setRevise(null);
    setError('');
    setTab('result');
    trackSaenggibuUxEvent('saenggibu_evidence_open', { surface, entry: initialSentence !== null ? 'sentence' : 'summary' });
  }, [open, initialSentence, surface]);

  const firstContentRef = useRef(true);
  useEffect(() => {
    if (firstContentRef.current) {
      firstContentRef.current = false;
      return;
    }
    setRevise(null);
    setSelectedUnit(null);
    setSelectedSentence(null);
    setError('');
  }, [content]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 4000);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (!open || !nav) return undefined;
    const onKey = (event) => {
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || busy) return;
      if (event.key === 'ArrowLeft' && nav.index > 0) nav.onPrev();
      if (event.key === 'ArrowRight' && nav.index < nav.total - 1) nav.onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, nav, busy]);

  const litUnits = useMemo(() => new Set(current ? current.units : []), [current]);
  const litSentences = useMemo(() => new Set(selectedUnit ? (usedBy.get(selectedUnit) || []) : []), [selectedUnit, usedBy]);

  const selectSentence = (index) => {
    setSelectedSentence((prev) => (prev === index ? null : index));
    setSelectedUnit(null);
    setRevise(null);
    setError('');
    trackSaenggibuUxEvent('saenggibu_evidence_sentence_click', { surface, stage: sentencePieces[index]?.how });
  };

  const selectUnit = (unitId) => {
    setSelectedUnit(unitId);
    setSelectedSentence(null);
    setRevise(null);
    setError('');
  };

  const runRevision = useCallback(async (action, extra = '') => {
    if (!current || !onRequestRevision) return;
    const instruction = action.id === 'request' ? extra : action.instruction;
    const bytes = calculateNEISBytes(current.text);
    setError('');
    setRevise({ kind: action.id, loading: true, sentenceIndex: current.index });
    trackSaenggibuUxEvent('saenggibu_evidence_revise', { surface, stage: action.id });
    try {
      const after = await onRequestRevision({
        scope: 'sentence',
        sentenceText: current.text,
        sentenceIndex: current.index,
        instruction,
        targetBytes: action.id === 'shorter' && bytes >= 160 ? Math.round(bytes * 0.65) : undefined,
      });
      if (!after || after === content) {
        setRevise(null);
        setError('바뀐 내용이 없어요. 다른 방법으로 다시 시도해주세요.');
        return;
      }
      setRevise({ kind: action.id, loading: false, after, sentenceIndex: current.index });
    } catch (requestError) {
      setRevise(null);
      setError(requestError?.message && !/HTTP error/.test(requestError.message)
        ? requestError.message
        : '지금 문장을 고치지 못했어요. 잠시 후 다시 시도해주세요.');
    }
  }, [current, onRequestRevision, content, surface]);

  const startAction = (action) => {
    if (!current) return;
    setError('');
    if (action.id === 'request') {
      setRequestText('');
      setRevise({ kind: 'request', loading: false, editing: true, sentenceIndex: current.index });
      return;
    }
    if (action.id === 'manual') {
      setManualDraft(current.text.trim());
      setRevise({ kind: 'manual', loading: false, editing: true, sentenceIndex: current.index });
      return;
    }
    if (action.id === 'remove') {
      const after = `${content.slice(0, current.start)}${content.slice(current.end)}`.replace(/[ \t]{2,}/g, ' ').replace(/^\s+/, '');
      setRevise({ kind: 'remove', loading: false, after, sentenceIndex: current.index });
      trackSaenggibuUxEvent('saenggibu_evidence_revise', { surface, stage: 'remove' });
      return;
    }
    runRevision(action);
  };

  const confirmManual = () => {
    const nextSentence = manualDraft.trim();
    if (!current || !nextSentence) return;
    const lead = current.text.match(/^\s*/)[0];
    const after = `${content.slice(0, current.start)}${lead}${nextSentence}${content.slice(current.end)}`;
    trackSaenggibuUxEvent('saenggibu_evidence_revise', { surface, stage: 'manual' });
    setRevise({ kind: 'manual', loading: false, after, sentenceIndex: current.index });
  };

  const insertUnit = async (unit) => {
    if (!onRequestRevision) return;
    setError('');
    setRevise({ kind: 'insert', loading: true, unitId: unit.id });
    trackSaenggibuUxEvent('saenggibu_evidence_revise', { surface, stage: 'insert' });
    try {
      const after = await onRequestRevision({
        scope: 'whole',
        instruction: `아래 내용을 이 글에 자연스럽게 한 문장으로 더해 주세요. 다른 문장은 최대한 그대로 둡니다.\n${unit.text}`,
      });
      if (!after || after === content) {
        setRevise(null);
        setError('바뀐 내용이 없어요. 다시 시도해주세요.');
        return;
      }
      setRevise({ kind: 'insert', loading: false, after, unitId: unit.id });
      setTab('result');
    } catch {
      setRevise(null);
      setError('지금 내용을 넣지 못했어요. 잠시 후 다시 시도해주세요.');
    }
  };

  const apply = async () => {
    if (!revise?.after || !onApply) return;
    setRevise((prev) => ({ ...prev, loading: true, applying: true }));
    const ok = await onApply(revise.after);
    if (!ok) {
      setRevise((prev) => ({ ...prev, loading: false, applying: false }));
      setError('화면에는 바꿨지만 기록 저장은 못 했어요. 잠시 후 다시 시도해주세요.');
      return;
    }
    const kind = revise.kind;
    setRevise(null);
    setSelectedSentence(null);
    setNotice(kind === 'remove' ? '문장을 빼고 기록에 저장했어요' : '바꾼 문장을 기록에 저장했어요');
  };

  const preview = revise?.after ? (
    <div className="sgev-preview" data-testid="sgev-preview">
      <p className="sgev-preview__title">
        {revise.kind === 'remove' ? '이 문장을 빼면 이렇게 돼요' : revise.kind === 'insert' ? '이렇게 넣었어요' : '이렇게 바꿨어요'}
        <span className="sgev-preview__bytes">{calculateNEISBytes(content)}B → {calculateNEISBytes(revise.after)}B</span>
      </p>
      {revise.kind === 'insert'
        ? <p className="sgev-preview__text">{revise.after}</p>
        : (
          <>
            <p className="sgev-preview__before">{current?.text.trim()}</p>
            {revise.kind !== 'remove' && <p className="sgev-preview__after">{replacedSentence(content, revise.after, current)}</p>}
          </>
        )}
      <div className="sgev-preview__actions">
        <button type="button" className="sgev-btn sgev-btn--ghost" disabled={revise.loading} onClick={() => setRevise(null)}>
          그대로 두기
        </button>
        <button type="button" className="sgev-btn sgev-btn--primary" disabled={revise.loading} onClick={apply} data-testid="sgev-apply">
          {revise.applying ? '저장하는 중' : revise.kind === 'remove' ? '문장 빼기' : '이대로 바꾸기'}
        </button>
      </div>
    </div>
  ) : null;

  const sentenceCard = current && (
    <div className="sgev-card" data-testid="sgev-card">
      <p className="sgev-card__how">{describeHow(current, unitsById)}</p>
      {current.units.length > 0 && (
        <ul className="sgev-card__units">
          {[...current.units].sort((a, b) => (numbers.get(a) || 0) - (numbers.get(b) || 0)).map((unitId) => {
            const hit = unitsById.get(unitId);
            if (!hit) return null;
            return (
              <li key={unitId}>
                <span className="sgev-num" aria-hidden="true">{numbers.get(unitId)}</span>
                <span className="sgev-card__unit-text">{hit.unit.text}</span>
                <span className="sgev-card__unit-src">{hit.source.label}</span>
              </li>
            );
          })}
        </ul>
      )}
      {canRevise && !revise?.after && (
        <>
          {revise?.kind === 'request' && revise.editing ? (
            <form className="sgev-inline-form" onSubmit={(event) => { event.preventDefault(); if (requestText.trim()) runRevision(REVISE_ACTIONS[2], requestText.trim()); }}>
              <input
                autoFocus
                className="sgev-input"
                value={requestText}
                onChange={(event) => setRequestText(event.target.value)}
                placeholder="예: 실제로는 모둠장이었어요"
                maxLength={300}
                aria-label="이 문장을 어떻게 고칠지 적기"
              />
              <button type="submit" className="sgev-btn sgev-btn--primary" disabled={!requestText.trim()}>문장 고치기</button>
            </form>
          ) : revise?.kind === 'manual' && revise.editing ? (
            <div className="sgev-inline-form sgev-inline-form--stack">
              <textarea
                autoFocus
                className="sgev-input sgev-textarea"
                value={manualDraft}
                onChange={(event) => setManualDraft(event.target.value)}
                rows={3}
                aria-label="문장 직접 고치기"
              />
              <div className="sgev-preview__actions">
                <button type="button" className="sgev-btn sgev-btn--ghost" onClick={() => setRevise(null)}>취소</button>
                <button type="button" className="sgev-btn sgev-btn--primary" disabled={!manualDraft.trim()} onClick={confirmManual}>고친 문장 보기</button>
              </div>
            </div>
          ) : (
            <div className="sgev-actions" role="group" aria-label="이 문장 고치기">
              {REVISE_ACTIONS.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  className={`sgev-chip${action.id === 'remove' ? ' sgev-chip--quiet' : ''}`}
                  disabled={busy}
                  onClick={() => startAction(action)}
                >
                  {revise?.loading && revise.kind === action.id ? '고치는 중' : action.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}
      {preview}
    </div>
  );

  const insertPreview = revise?.kind === 'insert' ? (
    revise.loading
      ? <div className="sgev-card sgev-card--busy" aria-live="polite">내용을 넣은 문장을 만들고 있어요</div>
      : <div className="sgev-card">{preview}</div>
  ) : null;

  const usedSummary = item?.summary;
  const description = status === 'ready' && usedSummary
    ? (usedSummary.total > 0 ? `넣은 내용 ${usedSummary.total}개 중 ${usedSummary.used}개를 썼어요` : '넣은 내용을 정리해 썼어요')
    : '어떤 내용으로 썼는지 찾고 있어요';

  return (
    <Dialog
      open={open}
      onClose={() => {
        trackSaenggibuUxEvent('saenggibu_evidence_close', { surface });
        onClose?.();
      }}
      size="lg"
      flush
      title="근거 보기"
      description={description}
      panelClassName="sgev-modal"
      testId="sgev-dialog"
      closeLabel="근거 보기 닫기"
    >
      <div className="sgev-layout">
        {(subtitle || nav) && (
          <div className="sgev-toolbar">
            <span className="sgev-toolbar__title">{subtitle}</span>
            {nav && (
              <div className="sgev-nav" aria-label="학생 이동">
                <button type="button" className="sgev-nav__btn" aria-label="이전 학생" disabled={nav.index <= 0 || busy} onClick={nav.onPrev}>‹</button>
                <span className="sgev-nav__pos">{nav.index + 1} / {nav.total}</span>
                <button type="button" className="sgev-nav__btn" aria-label="다음 학생" disabled={nav.index >= nav.total - 1 || busy} onClick={nav.onNext}>›</button>
              </div>
            )}
          </div>
        )}
        <div className="sgev-tabs" role="tablist" aria-label="근거 보기 화면">
          <button type="button" role="tab" aria-selected={tab === 'result'} className={tab === 'result' ? 'is-active' : ''} onClick={() => setTab('result')}>생기부</button>
          <button type="button" role="tab" aria-selected={tab === 'sources'} className={tab === 'sources' ? 'is-active' : ''} onClick={() => setTab('sources')}>내가 넣은 내용</button>
        </div>
        <div className={`sgev-panels sgev-panels--${tab}`}>
          <section className="sgev-pane sgev-pane--sources" aria-label="내가 넣은 내용">
            <h3 className="sgev-pane__title">내가 넣은 내용</h3>
            <SourceList
              sources={sources}
              numbers={numbers}
              usedBy={usedBy}
              selectedUnit={selectedUnit}
              highlighted={litUnits}
              onSelectUnit={(unitId) => { selectUnit(unitId); if (unitId && usedBy.get(unitId)?.length) setTab('result'); }}
              onInsert={insertUnit}
              canRevise={canRevise}
              busy={busy}
            />
          </section>
          <section className="sgev-pane sgev-pane--result" aria-label="생기부">
            <h3 className="sgev-pane__title">
              생기부
              <span className="sgev-pane__hint">{pieces ? '문장을 누르면 쓴 내용이 보여요' : ''}</span>
            </h3>
            <div className="sgev-result" data-testid="sgev-result">
              {pieces ? pieces.map((piece, index) => {
                if (piece.type === 'gap') return <React.Fragment key={`g${index}`}>{piece.text}</React.Fragment>;
                const marks = sentenceNumbers(piece.units, numbers);
                const isActive = selectedSentence === piece.index;
                return (
                  <span
                    key={`s${piece.index}`}
                    role="button"
                    tabIndex={0}
                    className={[
                      'sgev-sentence',
                      isActive && 'is-active',
                      litSentences.has(piece.index) && 'is-lit',
                      piece.how === 'edited' && 'is-edited',
                    ].filter(Boolean).join(' ')}
                    onClick={() => selectSentence(piece.index)}
                    onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); selectSentence(piece.index); } }}
                    data-testid="sgev-sentence"
                  >
                    {piece.text}
                    <span className={`sgev-cite sgev-cite--static${marks.length ? '' : ' sgev-cite--summary'}`} data-label={marks.length ? marks.join(',') : '정리'} aria-hidden="true" />
                  </span>
                );
              }) : (
                <>
                  {content}
                  <p className="sgev-wait" aria-live="polite">
                    {status === 'failed' ? '지금 근거를 불러오지 못했어요. 잠시 후 다시 열어주세요.' : '어떤 내용으로 썼는지 찾고 있어요'}
                  </p>
                </>
              )}
            </div>
            {error && <p className="sgev-error" role="alert">{error}</p>}
            {notice && !error && <p className="sgev-notice" role="status">{notice}</p>}
            {sentenceCard || insertPreview || (pieces && !selectedUnit && (
              <p className="sgev-hint">번호는 왼쪽 &lsquo;내가 넣은 내용&rsquo;의 번호예요. <span className="sgev-cite sgev-cite--summary sgev-cite--static" data-label="정리" aria-hidden="true" />는 전체를 정리한 문장이에요.</p>
            ))}
          </section>
        </div>
      </div>
    </Dialog>
  );
}

/** 고른 문장 자리에 들어간 새 문장. 앞뒤 글이 그대로가 아니면 전체 글을 돌려준다. */
export function replacedSentence(before, after, sentence) {
  if (!sentence) return after.trim();
  const tail = before.length - sentence.end;
  const same = after.startsWith(before.slice(0, sentence.start)) && after.endsWith(before.slice(sentence.end));
  if (!same || after.length - tail < sentence.start) return after.trim();
  return after.slice(sentence.start, after.length - tail).trim();
}
